import React from "react";
import { Phone, Mail, Send, MessageCircle, MessageSquare, Radio, Instagram, Youtube } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSite } from "../context/SiteContext";
import { useBooking } from "../context/BookingContext";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { localBusinessLd } from "../lib/seo";

export default function Contacts() {
  const { t, lang } = useLang();
  const site = useSite();
  const { openBooking } = useBooking();
  const c = site?.settings?.contacts || {};

  const items = [
    c.phone && { icon: Phone, label: lang === "ru" ? "Позвонить" : "Call", value: c.phone, href: `tel:${c.phone}` },
    c.phone && { icon: MessageSquare, label: lang === "ru" ? "SMS" : "Text (SMS)", value: c.phone, href: `sms:${c.phone}` },
    c.max && { icon: MessageCircle, label: lang === "ru" ? "MAX" : "MAX", value: c.max, href: "https://max.ru/u/f9LHodD0cOKIQ3CkI05ZDHFq3wnbhdSpV1OrgE68OxpVwSEtfmM8rQLNqBE" },
    c.telegram_dm && { icon: Send, label: lang === "ru" ? "Telegram — написать" : "Telegram — message", value: "@argalialtai", href: c.telegram_dm },
    c.telegram_channel && { icon: Radio, label: lang === "ru" ? "Telegram-канал" : "Telegram channel", value: "@argali_altai", href: c.telegram_channel },
    c.whatsapp && { icon: MessageCircle, label: "WhatsApp", value: c.phone || "+7 994 110-25-11", href: c.whatsapp },
    c.instagram && { icon: Instagram, label: "Instagram", value: "@argalialtai", href: c.instagram },
    c.youtube && { icon: Youtube, label: "YouTube", value: "@ArgaliAltai", href: c.youtube },
    c.email && { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}` },
  ].filter(Boolean);

  return (
    <div data-testid="contacts-page" style={{ background: "#F4F0E7", minHeight: "100vh" }}>
      <Seo path="/contacts" lang={lang} title={lang === "ru" ? "Контакты" : "Contacts"} description={lang === "ru" ? "Свяжитесь с АРГАЛИ — телефон, MAX, Telegram, WhatsApp, Instagram, YouTube, email. Индивидуальные экскурсии по Республике Алтай." : "Contact АРГАЛИ — phone, MAX, Telegram, WhatsApp, Instagram, YouTube, email. Private excursions across the Altai Republic."} jsonLd={localBusinessLd(lang)} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-32 pb-24">
        <Reveal>
          <img src="/brand/argali_logo_ru.png" alt="Логотип АРГАЛИ — этнотуризм, дикая природа, горы" width="811" height="915" className="h-40 md:h-52 w-auto mb-10" />
        </Reveal>
        <Reveal>
          <p className="eyebrow" style={{ color: "#A96818" }}>{t("nav_contacts")}</p>
          <h1 className="display display-lg text-argali-obsidian mt-3">{lang === "ru" ? "Напишите нам." : "Get in touch."}</h1>
          <p className="text-argali-brown/70 mt-5 max-w-lg">{lang === "ru" ? "Мы ответим и поможем собрать маршрут под ваши даты и интересы." : "We'll reply and help design a route for your dates and interests."}</p>
        </Reveal>

        {c.phone && (
          <div className="flex flex-wrap gap-4 mt-10">
            <a href={`tel:${c.phone}`} data-testid="contacts-call" className="btn-bronze"><Phone size={16} /> {lang === "ru" ? "Позвонить" : "Call"} {c.phone}</a>
            <a href="https://max.ru/u/f9LHodD0cOKIQ3CkI05ZDHFq3wnbhdSpV1OrgE68OxpVwSEtfmM8rQLNqBE" target="_blank" rel="noreferrer" data-testid="contacts-max" className="btn-outline !text-argali-brown" style={{ borderColor: "rgba(52,33,22,0.3)" }}><MessageCircle size={16} /> {lang === "ru" ? "Написать в MAX" : "Message on MAX"}</a>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {items.map((it) => (
            <a key={it.label} href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-${it.label}`} className="group flex items-center gap-5 border border-argali-brown/20 p-6 hover:border-argali-bronze transition-colors" style={{ background: "#FAFAF7" }}>
              <it.icon size={24} className="text-argali-bronze" />
              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50">{it.label}</p>
                <p className="text-argali-obsidian text-lg mt-0.5">{it.value}</p>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10">
          <button onClick={() => openBooking(null)} className="btn-bronze" data-testid="contacts-request">{t("cta_request")}</button>
        </div>

        <div className="mt-16 text-sm text-argali-brown/60 border-t border-argali-brown/15 pt-8">
          <p>{c.org_name}{c.ogrnip ? ` · ОГРНИП ${c.ogrnip}` : ""}</p>
          <p className="mt-1">{lang === "ru" ? "Республика Алтай, Россия" : "Altai Republic, Russia"}</p>
        </div>
      </div>
    </div>
  );
}
